import { Slide } from 'yet-another-react-lightbox';
import { eCalendarEventTypes } from '../../pages/Agenda/components/Calendar/Calendar.enums';
import { eLeadStatus } from '../../providers/SocketsProvider/Sockets.enums';
import { tLeadUpdatedEvent } from '../../providers/SocketsProvider/Sockets.types';
import { resolveAttachmentFilename } from '../../utils/downloadAttachment';
import { getMediaTypeImage } from '../../utils/GetMediaType';
import { normalizeLeadTags } from '../AdvanceSearch/AdvanceSearchModal.functions';
import { tAdvanceSearchLead } from '../AdvanceSearch/AdvanceSearchModal.types';
import {
  CalendarEventType,
  eAction,
  eField,
  EventType,
  MediaType,
} from './Chat.enum';
import {
  eScheduledTemplateOutcome,
  tAttachment,
  tChatEvent,
  tChatEvents,
  tChatSwitchCacheClass,
  tParticipant,
  tUpdateEventAttachment,
} from './Chat.types';
import {
  isDocumentOrPdf,
  isImage,
  isVideoOrAudio,
} from './components/MessageBar/components/Thumbnail/Thumbnail.functions';

export function updateEventAttachmentParser(
  event: tAttachment,
): tUpdateEventAttachment {
  return {
    media_type: getMediaTypeImage(event.mimeType ?? MediaType.DOCUMENT),
    url: event.url || '',
    filename: event.filename || undefined,
    mimetype: event.mimeType || '',
    latitude: event.latitude || undefined,
    longitude: event.longitude || undefined,
    decryption:
      event.decryption?.encryptedKey && event.decryption.iv
        ? event.decryption
        : undefined,
    vcard: event.vcard || undefined,
    duration: event.duration || undefined,
  };
}

export function convertToCalendarEventType(
  eventType: eCalendarEventTypes,
): CalendarEventType {
  switch (eventType) {
    case eCalendarEventTypes.MEETING:
      return CalendarEventType.MEETING;
    case eCalendarEventTypes.CALL:
      return CalendarEventType.CALL;
    case eCalendarEventTypes.TASK:
      return CalendarEventType.TASK;
    default:
      throw new Error(`Tipo de evento no soportado: ${eventType}`);
  }
}

export function convertDurationToMinutes(durationString: string): number {
  if (!/^\d{1,2}:\d{1,2}$/.test(durationString)) {
    return 0;
  }

  const [hours, minutes] = durationString.split(':').map(Number);
  return hours * 60 + minutes;
}

export function parseSlides(attachments: tAttachment[]) {
  const slides: Slide[] = attachments.map((file) => {
    // BUG-1144: the download plugin takes the saved file's name from
    // `download.filename` only — `title`, `alt` and `description` never reach
    // it, which is why every save used to land under the storage ULID.
    const download = {
      url: file.url ?? '',
      filename: resolveAttachmentFilename(file),
    };

    if (file?.mimeType && isDocumentOrPdf(file?.mimeType)) {
      return {
        src: '',
        download,
        alt: file.filename ?? '',
        title: file.filename ?? '',
        description: file.filename,
      };
    }
    if (file?.mimeType && isVideoOrAudio(file?.mimeType)) {
      return {
        type: 'video',
        autoPlay: true,
        poster: '',
        sources: [
          {
            src: file.url || '',
            type: file.mimeType || 'video/mp4',
          },
        ],
        download,
      };
    }
    if (file?.mimeType && isImage(file?.mimeType)) {
      return {
        src: file.url || '',
        alt: file.filename ?? '',
        title: file.filename ?? '',
        description: file.filename,
        type: 'image',
        download,
      };
    }

    return {
      src: file.url ?? '',
      alt: file.filename ?? '',
      title: file.filename ?? '',
      description: file.filename,
      download,
    };
  });

  return slides;
}

export function formatDateWithDay(date: string) {
  if (!date) {
    return 'na.';
  }
  const dateObj = new Date(date);
  return dateObj.toLocaleDateString('es-MX', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

export function formatDate(date: string) {
  if (!date) {
    return 'na.';
  }
  const dateObj = new Date(date);
  return dateObj.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function formatTime(date: string) {
  if (!date) {
    return 'na.';
  }
  const dateObj = new Date(date);
  return dateObj.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: 'numeric',
  });
}

export function formatDateShort(date: string) {
  if (!date) {
    return 'na.';
  }
  const dateObj = new Date(date);
  return dateObj.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

export function showStatusEmoji(status: string) {
  switch (status) {
    case eLeadStatus.NEUTRAL:
      return '';
    case eLeadStatus.URGENT:
      return '🔥 ';
    case eLeadStatus.IMPORTANT:
      return '⭐ ';
    case eLeadStatus.PENDING:
      return '⌛ ';
    case eLeadStatus.STUCK:
      return '🧊 ';
    default:
      return '';
  }
}

export function parseField(field: eField) {
  switch (field) {
    case eField.NAME:
      return 'name';
    case eField.EMAIL:
      return 'email';
    case eField.PROFILE_PICTURE:
      return 'profilePicture';
    case eField.COMPANY:
      return 'company';
    case eField.ASSIGNEE_AUTH_ID:
      return 'assignedAuthId';
    case eField.ARCHIVED:
      return 'leadStatus';
    case eField.VALUE:
      return 'value';
    case eField.STAGE:
      return 'stage';
    case eField.FUNNEL:
      return 'funnel';
    case eField.FOLLOW_UP:
      return 'followUp';
    case eField.MESSAGE:
      return 'message';
    case eField.NOTE:
      return 'note';
    case eField.IN_INBOX:
      return 'inboxStatus';
    case eField.LEAD:
      return 'lead';
  }
}

export function parseAction(action: eAction) {
  switch (action) {
    case eAction.CREATE:
      return 'created';
    case eAction.UPDATE:
      return 'changed';
    case eAction.DELETE:
      return 'deleted';
    case eAction.EXECUTE:
      return 'executed';
  }
}

export function chatLeadParser(
  leadUpdatedEvent: tLeadUpdatedEvent,
  currentLead: tAdvanceSearchLead,
): tAdvanceSearchLead {
  const response = leadUpdatedEvent.response;

  return {
    ...currentLead,
    active: response?.active ?? currentLead.active,
    id: response?.uuid ?? currentLead.id,
    name: response?.full_name ?? currentLead.name ?? '',
    handle: response?.handle ?? currentLead.handle ?? '',
    email: response?.email ?? currentLead.email ?? '',
    company: response?.company ?? currentLead.company ?? '',
    profilePicture: response?.profile_picture ?? currentLead.profilePicture ?? '',
    value: response?.value ?? currentLead.value ?? 0,
    revenue: response?.revenue ?? currentLead.revenue ?? 0,
    stage: {
      id: response?.stage?.uuid ?? currentLead.stage?.id ?? '',
      name: response?.stage?.name ?? currentLead.stage?.name ?? '',
    },
    status: {
      id: response?.status?.uuid ?? currentLead.status?.id ?? '',
      name: response?.status?.name ?? currentLead.status?.name ?? '',
    },
    funnel: response?.funnel
      ? {
          uuid: response.funnel.uuid ?? currentLead.funnel?.uuid ?? '',
          name: response.funnel.name ?? currentLead.funnel?.name ?? '',
          isDefault: currentLead.funnel?.isDefault ?? false,
        }
      : currentLead.funnel ?? { uuid: '', name: '', isDefault: false },
    assignedUser: response?.user
      ? {
          authID: response.user.auth_id ?? '',
          name: response.user.name ?? '',
        }
      : currentLead.assignedUser,
    tags: response?.tags ? normalizeLeadTags(response.tags) : currentLead.tags ?? [],
    leadbotState: response?.leadbot_state ?? currentLead.leadbotState,
    chat: currentLead.chat
      ? {
          ...currentLead.chat,
          unreadCount:
            response?.message?.unread_messages ??
            currentLead.chat.unreadCount ??
            0,
        }
      : undefined,
    hasCalendarEvent: currentLead.hasCalendarEvent ?? false,
  };
}

export function mergeByHandle(
  chatEvents: tChatEvent[],
  participants: tParticipant[],
) {
  const updatedMessages = chatEvents.map((event) => {
    const handle = event?.message?.participant?.handle;

    if (handle) {
      const user = participants.find((u) => u.handle === handle);

      if (user && event?.message?.participant) {
        event.message.participant.name = user.name;
      }
    }

    return event;
  });

  return updatedMessages;
}

// Default cap on the module-scoped warm-switch cache. Without a bound, "warm"
// degrades over long sessions (a chat opened at 9 AM is still "warm" at 6 PM
// after the real JS caches have been evicted) and the Set grows with every
// chat visited. 500 is a conservative LRU size for agents who rarely revisit
// chats beyond their recent working set.
export const DEFAULT_CHAT_SWITCH_WARM_CACHE_MAX = 500;

export const classifyChatSwitchCacheClass = (
  chatId: string,
  cache: Set<string>,
  maxEntries: number = DEFAULT_CHAT_SWITCH_WARM_CACHE_MAX,
): tChatSwitchCacheClass => {
  const wasWarm = cache.has(chatId);
  // Refresh LRU position: delete-then-add moves this entry to the end of the
  // Set's insertion order (JS Set preserves insertion order; set.add on an
  // existing key is a no-op for ordering).
  cache.delete(chatId);
  // Evict oldest entries when we're at or past the cap. `while` (not `if`)
  // makes this robust if the cap shrinks across calls.
  while (cache.size >= maxEntries) {
    const oldest = cache.values().next().value;
    if (oldest === undefined) break;
    cache.delete(oldest);
  }
  cache.add(chatId);
  return wasWarm ? 'warm' : 'cold';
};

export const getNetworkEffectiveType = (): string | undefined => {
  const nav = navigator as unknown as { connection?: { effectiveType?: string } };
  return nav.connection?.effectiveType;
};

const eventKey = (event: tChatEvent): string => event.providerId || event.id;

/**
 * Unwrap the 4 tolerated side-panel `Promise.allSettled` results into a
 * plain bag the main chat-load effect can hand off to setters. Endpoints
 * have two different shapes:
 *   - `getFollowUps` returns `{ data: { followUps } }` (wrapped axios body)
 *   - `getNotes` / `getLeadsalesAI` / `getFavoriteMessages` return the
 *     `tChatEvent[]` directly (unwrapped in the service)
 *
 * Any rejected result degrades to an empty array — matches the "self-heals
 * on next chat open" behavior of the orphan useEffects PR 3 deletes.
 * Notes are pre-filtered by `EventType.NOTE` so the caller can set the
 * notes slice directly.
 */
export type tTolerantSidePanelResults = readonly [
  PromiseSettledResult<{ data: { followUps: tChatEvent[] } }>,
  PromiseSettledResult<tChatEvent[]>,
  PromiseSettledResult<tChatEvent[]>,
  PromiseSettledResult<tChatEvent[]>,
];

export type tTolerantSidePanelState = {
  followUps: tChatEvent[];
  notes: tChatEvent[];
  leadsalesAI: tChatEvent[];
  rawFavoriteMessages: tChatEvent[] | null;
};

export const unwrapTolerantSidePanelResults = (
  results: tTolerantSidePanelResults,
): tTolerantSidePanelState => {
  const [followUpsResult, notesResult, leadsalesAIResult, favoritesResult] =
    results;
  return {
    followUps:
      followUpsResult.status === 'fulfilled'
        ? followUpsResult.value.data.followUps
        : [],
    notes:
      notesResult.status === 'fulfilled'
        ? notesResult.value.filter((e) => e.type === EventType.NOTE)
        : [],
    leadsalesAI:
      leadsalesAIResult.status === 'fulfilled' ? leadsalesAIResult.value : [],
    // Favorites needs async decryption downstream — pass raw or null to
    // signal "skip decryption, set empty" for the rejected case.
    rawFavoriteMessages:
      favoritesResult.status === 'fulfilled' ? favoritesResult.value : null,
  };
};

/**
 * Merge a fresh chatEvents fetch with the current local state, preserving
 * any socket-arrived events that landed on the client between the fetch
 * kicking off and setSelectedChat committing.
 *
 * Semantics:
 * 1. If `prev` is empty/undefined, return `incoming` unchanged (cold load).
 * 2. Otherwise, start from `prev.events` (preserves socket updates that may
 *    be newer than the fetch snapshot) and append any `incoming` event whose
 *    `providerId || id` is not already present.
 * 3. Take `incoming.cursor` when non-empty (fresh pagination state), else
 *    fall back to `prev.cursor`.
 *
 * Closes the socket-clobber race window that PR 3 widens by running Phase 4
 * side-panel fetches in parallel with Phase 3: the extra wait time before
 * `setSelectedChat` is a larger window for `chatUpdated` / `messageInserted`
 * socket events to append to `prev` that a naive re-set would drop.
 */
export const mergeChatEventsByEventId = (
  prev: tChatEvents | undefined,
  incoming: tChatEvents,
): tChatEvents => {
  if (!prev || prev.events.length === 0) {
    return incoming;
  }

  const prevKeys = new Set<string>(prev.events.map(eventKey));
  const appended: tChatEvent[] = [];
  for (const event of incoming.events) {
    if (!prevKeys.has(eventKey(event))) {
      appended.push(event);
    }
  }

  return {
    cursor: incoming.cursor || prev.cursor,
    events: appended.length === 0 ? prev.events : [...prev.events, ...appended],
  };
};

/**
 * BUG-741: resolves what a scheduled-template card should actually say.
 *
 * Before this, both card surfaces rendered a single unconditional
 * "Se enviará <fecha>" regardless of whether the underlying
 * whatsapp-business-service campaign had sent, failed, or never run — so the card
 * promised a future send forever, which is the symptom the ticket was opened for.
 *
 * `state` is the chat event's lifecycle state, written by messaging's
 * `ScheduledTemplateReconciler` once the real campaign reaches a terminal status.
 */
export const scheduledTemplateOutcome = (
  event: tChatEvent,
): eScheduledTemplateOutcome => {
  if (event.state === 'ERROR') return eScheduledTemplateOutcome.FAILED;
  if (
    event.state === 'SENT' ||
    event.state === 'DELIVERY' ||
    event.state === 'READ'
  ) {
    return eScheduledTemplateOutcome.SENT;
  }
  return eScheduledTemplateOutcome.PENDING;
};

/**
 * The human explanation for a failed scheduled template, taken from the campaign
 * failure reason the reconciler attached to the event. Falls back to generic copy
 * so a failed card is never rendered blank — an error with no explanation is the
 * exact limbo this fix removes.
 */
export const scheduledTemplateFailureReason = (event: tChatEvent): string => {
  const context = event.errors?.find((e) => e.error?.context)?.error?.context;
  return context?.trim() || 'El envío programado no pudo completarse.';
};

// chatbot-service reports a turn that billing paused as a
// `smartleadbot.session.gate_failed` whose gate_name is this prefix plus the
// pause reason. The reason set is open, so callers match the prefix.
const BILLING_GATE_PREFIX = 'billing_';

/**
 * Whether a `smartleadbot.session.gate_failed` gate_name reports a billing
 * pause (any `billing_*` value).
 * @param {string} gateName - The event's gate_name.
 * @returns {boolean} True for a billing pause.
 */
export const isBillingGateName = (gateName: string): boolean =>
  gateName.startsWith(BILLING_GATE_PREFIX);

/**
 * The pause reason a billing gate_name carries, e.g. `cap_reached` for
 * `billing_cap_reached`.
 * @param {string} gateName - A gate_name that passes isBillingGateName.
 * @returns {string} The gate_name without the billing prefix.
 */
export const billingPauseReason = (gateName: string): string =>
  gateName.slice(BILLING_GATE_PREFIX.length);
